import { m as motion, useReducedMotion } from "framer-motion";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";

export interface StatBlock {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  source: string;
  /** mini visualization variant */
  viz: "bar" | "countdown" | "dots" | "line";
}

const stats: StatBlock[] = [
  { value: 82, suffix: "%", label: "av all internettrafik 2025 är video", source: "Cisco / Statista", viz: "bar" },
  { value: 17, prefix: "", suffix: " tiondelar", label: "har ni på er att fånga tummen i flödet (1,7 sek)", source: "Meta", viz: "countdown" },
  { value: 6, prefix: "×", suffix: "", label: "högre engagemang på rörligt vs statiskt på LinkedIn", source: "LinkedIn Business", viz: "dots" },
  { value: 80, prefix: "+", suffix: "%", label: "konverteringslyft på landningssidor med produktvideo", source: "Wyzowl 2024", viz: "line" },
];

export { stats as varforStats };

export function StatHero() {
  return (
    <div className="space-y-4 md:space-y-6">
      {stats.map((s, i) => (
        <StatRow key={s.label} stat={s} flip={i % 2 === 1} />
      ))}
    </div>
  );
}

function StatRow({ stat, flip }: { stat: StatBlock; flip: boolean }) {
  const reduce = useReducedMotion();
  return (
    <motion.article
      initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
      className={`grid items-center gap-8 rounded-3xl border border-white/10 bg-surface/60 p-8 md:grid-cols-[1.4fr_1fr] md:gap-12 md:p-12 ${flip ? "md:[&>*:first-child]:order-2" : ""}`}
    >
      <div>
        <div className="font-serif text-7xl font-semibold leading-none text-peach md:text-9xl">
          {stat.prefix}
          <LiveCountUp value={stat.value} />
          {stat.suffix && <span className="text-5xl md:text-7xl">{stat.suffix}</span>}
        </div>
        <p className="mt-6 max-w-md text-lg leading-relaxed text-foreground/90 md:text-xl">{stat.label}</p>
        <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          Källa · {stat.source}
        </div>
      </div>
      <div className="flex items-center justify-center">
        <MiniViz viz={stat.viz} value={stat.value} />
      </div>
    </motion.article>
  );
}

function MiniViz({ viz, value }: { viz: StatBlock["viz"]; value: number }) {
  const reduce = useReducedMotion();
  if (viz === "bar") {
    return (
      <div className="w-full max-w-xs space-y-3">
        <div className="flex items-center gap-3">
          <span className="w-16 text-[11px] uppercase tracking-widest text-muted-foreground">Video</span>
          <div className="h-3 flex-1 overflow-hidden rounded-full bg-white/5">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${value}%` }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="h-full rounded-full bg-peach"
            />
          </div>
          <span className="w-10 text-right text-xs text-peach">{value}%</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="w-16 text-[11px] uppercase tracking-widest text-muted-foreground">Övrigt</span>
          <div className="h-3 flex-1 overflow-hidden rounded-full bg-white/5">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${100 - value}%` }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.4, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="h-full rounded-full bg-white/20"
            />
          </div>
          <span className="w-10 text-right text-xs text-muted-foreground">{100 - value}%</span>
        </div>
      </div>
    );
  }
  if (viz === "countdown") {
    return (
      <div className="relative flex h-44 w-44 items-center justify-center">
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="44" stroke="rgba(255,255,255,0.08)" strokeWidth="3" fill="none" />
          <motion.circle
            cx="50" cy="50" r="44"
            stroke="var(--peach)" strokeWidth="3" fill="none"
            strokeDasharray="276.46" strokeLinecap="round"
            initial={{ strokeDashoffset: 276.46 }}
            whileInView={{ strokeDashoffset: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 1.7, ease: "linear" }}
          />
        </svg>
        <div className="text-center">
          <div className="font-mono text-3xl text-peach">1.7s</div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">Tummen avgör</div>
        </div>
      </div>
    );
  }
  if (viz === "dots") {
    return (
      <div className="flex flex-col gap-3">
        {[1, 6].map((n, row) => (
          <div key={n} className="flex items-center gap-2">
            <span className="w-16 text-[11px] uppercase tracking-widest text-muted-foreground">
              {row === 0 ? "Statiskt" : "Rörligt"}
            </span>
            <div className="flex gap-1.5">
              {Array.from({ length: n }).map((_, i) => (
                <motion.span
                  key={i}
                  initial={reduce ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 0.45, delay: i * 0.12 + row * 0.2 }}
                  className={`inline-block h-4 w-4 rounded-full ${row === 1 ? "bg-peach" : "bg-white/20"}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  // line
  return (
    <svg viewBox="0 0 200 100" className="w-full max-w-xs">
      <defs>
        <linearGradient id="lineg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--peach)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--peach)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d="M0,80 C30,82 50,70 80,60 C110,50 130,30 200,10 L200,100 L0,100 Z"
        fill="url(#lineg)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 1.2 }}
      />
      <motion.path
        d="M0,80 C30,82 50,70 80,60 C110,50 130,30 200,10"
        fill="none" stroke="var(--peach)" strokeWidth="2.5" strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
      />
    </svg>
  );
}
